import { useEffect, useState, useRef } from "react";
import { Calendar, CheckCircle, Clock, DollarSign, Shield, Star, Users, Server, Code, Smartphone, BarChart3, Settings, Clipboard, Lock, Search, Mail, Phone, MapPin, UserCheck, Crown, Triangle, Brain, Cloud, FileText, Activity, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import Navigation from "@/components/Navigation";
// import Footer from "@/components/Footer";
import ProjectForm from "@/components/ProjectForm";
import { Link } from "wouter";
import logoImage from "@assets/image_1749670714467.png";

export default function Home() {
  const [showStickyBtn, setShowStickyBtn] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);

  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef<number | null>(null);

  // Auto-scroll functionality
  const totalSlides = 8;
  
  const scrollToSlide = (slideIndex: number) => {
    if (carouselRef.current) {
      const cardWidth = 320; // w-80 = 320px
      const gap = 24; // space-x-6 = 24px
      // const paddingLeft = 48; // px-12 = 48px
      const scrollAmount = slideIndex * (cardWidth + gap);
      carouselRef.current.scrollTo({
        left: scrollAmount,
        behavior: 'smooth'
      });
      setCurrentSlide(slideIndex);
    }
  };

  const startAutoScroll = () => {
    if (autoScrollRef.current) clearInterval(autoScrollRef.current);
    
    autoScrollRef.current = window.setInterval(() => {
      nextSlide();
    }, 1000); // 1 second for quick cycling through profiles
  };

  const stopAutoScroll = () => {
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
      autoScrollRef.current = null;
    }
  };

  const nextSlide = () => {
    setCurrentSlide(prev => {
      const nextIndex = (prev + 1) % totalSlides;
      console.log(`Auto-scrolling to slide ${nextIndex}`);
      
      if (carouselRef.current) {
        const cardWidth = 320; // w-80 = 320px
        const gap = 24; // space-x-6 = 24px
        const scrollAmount = nextIndex * (cardWidth + gap);
        carouselRef.current.scrollTo({
          left: scrollAmount,
          behavior: 'smooth'
        });
      }
      
      return nextIndex;
    });
  };

  const prevSlide = () => {
    setCurrentSlide(prev => {
      const prevIndex = prev === 0 ? totalSlides - 1 : prev - 1;
      
      if (carouselRef.current) {
        const cardWidth = 320;
        const gap = 24;
        const scrollAmount = prevIndex * (cardWidth + gap);
        carouselRef.current.scrollTo({
          left: scrollAmount,
          behavior: 'smooth'
        });
      }
      
      return prevIndex;
    });
  };

  useEffect(() => {
    // Start auto-scrolling
    startAutoScroll();

    // Sticky button scroll handler
    const handleScroll = () => {
      setShowStickyBtn(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      stopAutoScroll();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const openProjectForm = () => {
    setShowProjectForm(true);
  };

  const closeProjectForm = () => {
    setShowProjectForm(false);
  };

  return (
    <div className="min-h-screen bg-dark-slate text-white overflow-x-hidden">
      {/* Navigation */}
      <Navigation onBookCall={openProjectForm} />

      {/* Hero Section */}
      <section className="min-h-screen flex items-center relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-cyber-purple rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-drift"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-cyber-blue rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-orbit" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-neon-pink rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-spiral" style={{animationDelay: '4s'}}></div>
        </div>
        
        <div className="container mx-auto px-6 py-24 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-black mb-6 text-shadow">
              Hire Pre-Vetted 
              <span className="text-cyber-purple"> FinTech Experts</span>{" "}
              in 7 Days Risk-Free
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed max-w-3xl mx-auto">
              Scale your tech team with elite South African IT talent. Only pay when you hire.
            </p>
            <div className="flex flex-col gap-6 justify-center items-center">
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a 
                  href="https://www.careers-page.com/vitosolutions/job/L347965W"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button 
                    className="bg-gradient-to-r from-neon-pink to-cyber-purple hover:from-cyber-purple hover:to-neon-pink text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 animate-glow"
                  >
                    <Users className="mr-3" />
                    Join us as Expert
                  </Button>
                </a>
                
                <Link href="/project-form">
                  <Button 
                    className="bg-cyber-gradient hover:bg-gradient-to-r hover:from-neon-pink hover:to-cyber-purple text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 animate-glow"
                  >
                    <Calendar className="mr-3" />
                    Start Hiring
                  </Button>
                </Link>
              </div>
              
              {/* Video Section */}
              <div className="w-full max-w-3xl mx-auto">
                <div className="relative aspect-video rounded-2xl overflow-hidden glassmorphism p-2">
                  <iframe
                    src="https://www.youtube.com/embed/rHxN9NqPzx0"
                    title="UnicornClub Demo"
                    className="w-full h-full rounded-xl"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                </div>
                <p className="text-center text-gray-400 mt-4 text-sm">
                  See how we help FinTech startups scale their teams
                </p>
              </div>
              
              <p className="text-sm text-gray-400 flex items-center">
                <Shield className="text-cyber-gold mr-2" />
                No subscription • No upfront cost • Pay only when you hire
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="py-8 border-t border-gray-800">
        <div className="container mx-auto px-6">
          <p className="text-center text-gray-400 mb-8 font-medium">Building trust through transparency and results</p>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="glassmorphism rounded-xl p-6 text-center">
                <Activity className="w-8 h-8 text-cyber-purple mx-auto mb-3" />
                <h4 className="font-bold text-white mb-2">100% Success Rate Goal</h4>
                <p className="text-gray-400 text-sm">We're committed to perfect placements from day one</p>
              </div>
              
              <div className="glassmorphism rounded-xl p-6 text-center">
                <Clock className="w-8 h-8 text-cyber-gold mx-auto mb-3" />
                <h4 className="font-bold text-white mb-2">7-Day Promise</h4>
                <p className="text-gray-400 text-sm">Fast matching without compromising quality</p>
              </div>
              
              <div className="glassmorphism rounded-xl p-6 text-center">
                <Shield className="w-8 h-8 text-neon-pink mx-auto mb-3" />
                <h4 className="font-bold text-white mb-2">Risk-Free</h4>
                <p className="text-gray-400 text-sm">Only pay on successful hire</p>
              </div>
              
              <div className="glassmorphism rounded-xl p-6 text-center">
                <FileText className="w-8 h-8 text-cyber-blue mx-auto mb-3" />
                <h4 className="font-bold text-white mb-2">Full Transparency</h4>
                <p className="text-gray-400 text-sm">Open about our process and progress</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-8">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-8">
            Why Choose <span className="text-cyber-purple">UnicornClub</span>?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-6">
            <div className="glassmorphism rounded-2xl p-8 text-center group hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-cyber-gradient rounded-full flex items-center justify-center mx-auto mb-6 group-hover:animate-pulse">
                <CheckCircle className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Pre-vetted Talent</h3>
              <p className="text-gray-300">Rigorously screened developers with proven FinTech experience</p>
            </div>
            
            <div className="glassmorphism rounded-2xl p-8 text-center group hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-cyber-gradient rounded-full flex items-center justify-center mx-auto mb-6 group-hover:animate-pulse">
                <Clock className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">7-Day Turnaround</h3>
              <p className="text-gray-300">Get matched with perfect candidates in just 7 days</p>
            </div>
            
            <div className="glassmorphism rounded-2xl p-8 text-center group hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-cyber-gradient rounded-full flex items-center justify-center mx-auto mb-6 group-hover:animate-pulse">
                <DollarSign className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">40% Cheaper</h3>
              <p className="text-gray-300">Premium talent at a fraction of Silicon Valley costs</p>
            </div>
            
            <div className="glassmorphism rounded-2xl p-8 text-center group hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-cyber-gradient rounded-full flex items-center justify-center mx-auto mb-6 group-hover:animate-pulse">
                <Shield className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">No Risk</h3>
              <p className="text-gray-300">No upfront cost, no subscription, pay only when you hire</p>
            </div>
          </div>
          
          {/* Second row of benefits */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-6 mt-8">
            <div className="glassmorphism rounded-2xl p-8 text-center group hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-cyber-gradient rounded-full flex items-center justify-center mx-auto mb-6 group-hover:animate-pulse">
                <Clipboard className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Payroll Handled</h3>
              <p className="text-gray-300">We manage all payroll, taxes, and compliance - you focus on building</p>
            </div>
            
            <div className="glassmorphism rounded-2xl p-8 text-center group hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-cyber-gradient rounded-full flex items-center justify-center mx-auto mb-6 group-hover:animate-pulse">
                <Users className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Native English</h3>
              <p className="text-gray-300">English as first or second language for clear communication</p>
            </div>
            
            <div className="glassmorphism rounded-2xl p-8 text-center group hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-cyber-gradient rounded-full flex items-center justify-center mx-auto mb-6 group-hover:animate-pulse">
                <Clock className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Timezone Overlap</h3>
              <p className="text-gray-300">Same timezone or minimum 6-hour overlap for seamless collaboration</p>
            </div>
            
            <div className="glassmorphism rounded-2xl p-8 text-center group hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-cyber-gradient rounded-full flex items-center justify-center mx-auto mb-6 group-hover:animate-pulse">
                <Settings className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Cultural Fit</h3>
              <p className="text-gray-300">Developers who understand Western business practices and startup culture</p>
            </div>
          </div>
          
          {/* Third row of benefits */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-6 mt-8">
            <div className="glassmorphism rounded-2xl p-8 text-center group hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-cyber-gradient rounded-full flex items-center justify-center mx-auto mb-6 group-hover:animate-pulse">
                <Star className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Strong Work Ethic</h3>
              <p className="text-gray-300">Dedicated professionals with proven track record of excellence</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-8 bg-gradient-to-r from-charcoal to-dark-slate">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-8">How It Works</h2>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center group">
                <div className="relative mb-8">
                  <div className="w-20 h-20 bg-unicorn-gradient rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-300">
                    <span className="text-2xl font-bold text-white">1</span>
                  </div>
                  <div className="absolute top-10 left-20 w-40 h-1 bg-gradient-to-r from-cyber-purple to-transparent hidden md:block"></div>
                </div>
                <h3 className="text-2xl font-bold mb-4">Tell Us Your Needs</h3>
                <p className="text-gray-300">Book a free discovery call and share your project requirements</p>
              </div>
              
              <div className="text-center group">
                <div className="relative mb-8">
                  <div className="w-20 h-20 bg-unicorn-gradient rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-300">
                    <span className="text-2xl font-bold text-white">2</span>
                  </div>
                  <div className="absolute top-10 left-20 w-40 h-1 bg-gradient-to-r from-cyber-purple to-transparent hidden md:block"></div>
                </div>
                <h3 className="text-2xl font-bold mb-4">Get Matched</h3>
                <p className="text-gray-300">We present 2-3 pre-vetted candidates within 7 days</p>
              </div>
              
              <div className="text-center group">
                <div className="mb-8">
                  <div className="w-20 h-20 bg-unicorn-gradient rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-300">
                    <span className="text-2xl font-bold text-white">3</span>
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-4">Start Building</h3>
                <p className="text-gray-300">Interview, hire, and onboard your new team member</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Developer Profiles Showcase */}
      <section className="py-8 bg-gradient-to-r from-dark-slate to-charcoal overflow-hidden">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">Meet Our Elite Developers</h2>
          <p className="text-xl text-gray-300 text-center mb-12 max-w-3xl mx-auto">
            Pre-vetted South African tech professionals ready to join your team
          </p>
          
          {/* Horizontal Scrolling Container */}
          <div 
            className="relative max-w-full"
            onMouseEnter={stopAutoScroll}
            onMouseLeave={startAutoScroll}
          >
            {/* Left Arrow */}
            <button 
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-cyber-purple/80 hover:bg-cyber-purple text-white p-2 rounded-full transition-all duration-300 hover:scale-110"
              aria-label="Previous developer"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            {/* Right Arrow */}
            <button 
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-cyber-purple/80 hover:bg-cyber-purple text-white p-2 rounded-full transition-all duration-300 hover:scale-110"
              aria-label="Next developer"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            
            <div 
              ref={carouselRef}
              className="flex space-x-6 overflow-x-scroll scroll-smooth pb-4 px-12 scrollbar-hide" 
              style={{WebkitOverflowScrolling: 'touch'}}
            >
              {/* Developer 1 - Yolande */}
              <div className="glassmorphism rounded-2xl p-6 text-center group hover:scale-105 transition-all duration-300 flex-shrink-0 w-80 min-w-80">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden border-4 border-cyber-purple group-hover:border-neon-pink transition-colors">
                  <img 
                    src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop&crop=face" 
                    alt="Yolande" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold mb-1">YOLANDE</h3>
                <p className="text-cyber-purple font-semibold mb-3">SENIOR SCRUM MASTER</p>
                <div className="text-sm text-gray-300 space-y-1 mb-4">
                  <p>Agile • Scrum • Project Management</p>
                  <p>Team Leadership • Process Optimization</p>
                  <p className="text-cyber-gold">8+ Years Experience</p>
                </div>
              </div>
              
              {/* Developer 2 - David */}
              <div className="glassmorphism rounded-2xl p-6 text-center group hover:scale-105 transition-all duration-300 flex-shrink-0 w-80 min-w-80">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden border-4 border-cyber-purple group-hover:border-neon-pink transition-colors">
                  <img 
                    src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop&crop=face" 
                    alt="David" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold mb-1">DAVID</h3>
                <p className="text-cyber-purple font-semibold mb-3">SENIOR FULL-STACK DEVELOPER</p>
                <div className="text-sm text-gray-300 space-y-1 mb-4">
                  <p>React • Node.js • TypeScript • Python</p>
                  <p>AWS • Docker • PostgreSQL • MongoDB</p>
                  <p className="text-cyber-gold">10+ Years Experience</p>
                </div>
              </div>
              
              {/* Developer 3 - Edmore */}
              <div className="glassmorphism rounded-2xl p-6 text-center group hover:scale-105 transition-all duration-300 flex-shrink-0 w-80 min-w-80">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden border-4 border-cyber-purple group-hover:border-neon-pink transition-colors">
                  <img 
                    src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face" 
                    alt="Edmore" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold mb-1">EDMORE</h3>
                <p className="text-cyber-purple font-semibold mb-3">DEVOPS ENGINEER</p>
                <div className="text-sm text-gray-300 space-y-1 mb-4">
                  <p>Kubernetes • Docker • CI/CD</p>
                  <p>AWS • Azure • Infrastructure as Code</p>
                  <p className="text-cyber-gold">7+ Years Experience</p>
                </div>
              </div>
              
              {/* Developer 4 - Athini */}
              <div className="glassmorphism rounded-2xl p-6 text-center group hover:scale-105 transition-all duration-300 flex-shrink-0 w-80 min-w-80">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden border-4 border-cyber-purple group-hover:border-neon-pink transition-colors">
                  <img 
                    src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop&crop=face" 
                    alt="Athini" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold mb-1">ATHINI</h3>
                <p className="text-cyber-purple font-semibold mb-3">DATA ANALYST</p>
                <div className="text-sm text-gray-300 space-y-1 mb-4">
                  <p>Python • SQL • Power BI • Tableau</p>
                  <p>Machine Learning • Data Visualization</p>
                  <p className="text-cyber-gold">6+ Years Experience</p>
                </div>
              </div>

              {/* Developer 5 - Jack */}
              <div className="glassmorphism rounded-2xl p-6 text-center group hover:scale-105 transition-all duration-300 flex-shrink-0 w-80 min-w-80">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden border-4 border-cyber-purple group-hover:border-neon-pink transition-colors">
                  <img 
                    src="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop&crop=face" 
                    alt="Jack" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold mb-1">JACK</h3>
                <p className="text-cyber-purple font-semibold mb-3">BLOCKCHAIN DEVELOPER</p>
                <div className="text-sm text-gray-300 space-y-1 mb-4">
                  <p>Solidity • Web3.js • Ethereum • DeFi</p>
                  <p>Smart Contracts • Rust • Go</p>
                  <p className="text-cyber-gold">5+ Years Experience</p>
                </div>
              </div>

              {/* Developer 6 - Nora */}
              <div className="glassmorphism rounded-2xl p-6 text-center group hover:scale-105 transition-all duration-300 flex-shrink-0 w-80 min-w-80">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden border-4 border-cyber-purple group-hover:border-neon-pink transition-colors">
                  <img 
                    src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=400&h=400&fit=crop&crop=focalpoint&fp-x=0.5&fp-y=0.35" 
                    alt="Nora" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold mb-1">NORA</h3>
                <p className="text-cyber-purple font-semibold mb-3">CYBERSECURITY SPECIALIST</p>
                <div className="text-sm text-gray-300 space-y-1 mb-4">
                  <p>Penetration Testing • CISSP • CEH</p>
                  <p>Network Security • Incident Response</p>
                  <p className="text-cyber-gold">8+ Years Experience</p>
                </div>
              </div>

              {/* Developer 7 - Daniel */}
              <div className="glassmorphism rounded-2xl p-6 text-center group hover:scale-105 transition-all duration-300 flex-shrink-0 w-80 min-w-80">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden border-4 border-cyber-purple group-hover:border-neon-pink transition-colors">
                  <img 
                    src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop&crop=face" 
                    alt="Daniel" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold mb-1">DANIEL</h3>
                <p className="text-cyber-purple font-semibold mb-3">MOBILE APP DEVELOPER</p>
                <div className="text-sm text-gray-300 space-y-1 mb-4">
                  <p>React Native • Flutter • Swift • Kotlin</p>
                  <p>iOS • Android • Cross-platform</p>
                  <p className="text-cyber-gold">7+ Years Experience</p>
                </div>
              </div>

              {/* Developer 8 - Michael */}
              <div className="glassmorphism rounded-2xl p-6 text-center group hover:scale-105 transition-all duration-300 flex-shrink-0 w-80 min-w-80">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden border-4 border-cyber-purple group-hover:border-neon-pink transition-colors">
                  <img 
                    src="https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=400&fit=crop&crop=face" 
                    alt="Michael" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold mb-1">MICHAEL</h3>
                <p className="text-cyber-purple font-semibold mb-3">FINTECH SOLUTIONS ARCHITECT</p>
                <div className="text-sm text-gray-300 space-y-1 mb-4">
                  <p>Payment Systems • Banking APIs • PCI DSS</p>
                  <p>Microservices • Event-driven Architecture</p>
                  <p className="text-cyber-gold">12+ Years Experience</p>
                </div>
              </div>


            </div>
            
            {/* Scroll Indicators */}
            <div className="flex justify-center mt-6 space-x-2">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((index) => (
                <button
                  key={index}
                  onClick={() => scrollToSlide(index)}
                  className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                    currentSlide === index ? 'bg-cyber-purple' : 'bg-gray-600 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
          

        </div>
      </section>

      {/* Our Launch Story */}
      <section className="py-8">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-8">Why We're Launching UnicornClub</h2>
          <div className="max-w-4xl mx-auto text-center">
            <div className="glassmorphism rounded-2xl p-8 mb-8">
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                We're launching UnicornClub as a new service to prove what we believe: that South African developers are world-class talent that the global market hasn't fully discovered yet.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Our team has years of experience in tech recruitment and software development. We've seen firsthand how talented South African developers are, but we know trust is earned, not claimed.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                That's why we're starting fresh - to demonstrate our capabilities through results, not promises. Every successful placement builds our reputation and proves the quality of African tech talent.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="glassmorphism rounded-2xl p-6 text-center">
                <Shield className="w-12 h-12 text-cyber-gold mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Transparency First</h3>
                <p className="text-gray-300">We're upfront about being new because honesty builds better partnerships</p>
              </div>
              
              <div className="glassmorphism rounded-2xl p-6 text-center">
                <UserCheck className="w-12 h-12 text-cyber-gold mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Proven Team</h3>
                <p className="text-gray-300">Our founders have extensive experience in tech recruitment and development</p>
              </div>
              
              <div className="glassmorphism rounded-2xl p-6 text-center">
                <Crown className="w-12 h-12 text-cyber-gold mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Quality Focus</h3>
                <p className="text-gray-300">We're building our reputation one exceptional developer placement at a time</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Roles We Fill Section */}
      <section className="py-8 bg-gradient-to-r from-charcoal to-dark-slate">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-8">Roles We Fill</h2>
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <Server className="text-3xl text-cyber-blue mb-4 mx-auto" />
              <h3 className="font-bold mb-2">Backend Engineers</h3>
              <p className="text-sm text-gray-400">Node.js, Python, Java, .NET</p>
            </div>
            
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <Code className="text-3xl text-cyber-purple mb-4 mx-auto" />
              <h3 className="font-bold mb-2">Frontend Engineers</h3>
              <p className="text-sm text-gray-400">React, Vue, Angular, TypeScript</p>
            </div>
            
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <Smartphone className="text-3xl text-neon-pink mb-4 mx-auto" />
              <h3 className="font-bold mb-2">Mobile Developers</h3>
              <p className="text-sm text-gray-400">React Native, Flutter, Swift</p>
            </div>
            
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <Palette className="text-3xl text-cyber-gold mb-4 mx-auto" />
              <h3 className="font-bold mb-2">UI/UX Designers</h3>
              <p className="text-sm text-gray-400">Figma, Adobe XD, User Research</p>
            </div>
            
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <BarChart3 className="text-3xl text-cyber-gold mb-4 mx-auto" />
              <h3 className="font-bold mb-2">Data Engineers</h3>
              <p className="text-sm text-gray-400">Python, SQL, Spark, Kafka</p>
            </div>
            
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <Settings className="text-3xl text-cyber-blue mb-4 mx-auto" />
              <h3 className="font-bold mb-2">DevOps Engineers</h3>
              <p className="text-sm text-gray-400">AWS, Docker, Kubernetes</p>
            </div>
            
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <Clipboard className="text-3xl text-cyber-purple mb-4 mx-auto" />
              <h3 className="font-bold mb-2">Product Managers</h3>
              <p className="text-sm text-gray-400">Agile, Scrum, Product Strategy</p>
            </div>
            
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <Lock className="text-3xl text-neon-pink mb-4 mx-auto" />
              <h3 className="font-bold mb-2">Security Engineers</h3>
              <p className="text-sm text-gray-400">Cybersecurity, Penetration Testing</p>
            </div>
            
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <Search className="text-3xl text-cyber-gold mb-4 mx-auto" />
              <h3 className="font-bold mb-2">QA Engineers</h3>
              <p className="text-sm text-gray-400">Automation, Manual Testing</p>
            </div>
            
            {/* New roles from the image */}
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <UserCheck className="text-3xl text-cyber-purple mb-4 mx-auto" />
              <h3 className="font-bold mb-2">Product Owner</h3>
              <p className="text-sm text-gray-400">Product Strategy, Requirements</p>
            </div>
            
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <Crown className="text-3xl text-cyber-gold mb-4 mx-auto" />
              <h3 className="font-bold mb-2">Tech Lead</h3>
              <p className="text-sm text-gray-400">Architecture, Team Leadership</p>
            </div>
            
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <Triangle className="text-3xl text-cyber-blue mb-4 mx-auto" />
              <h3 className="font-bold mb-2">Solutions Architect</h3>
              <p className="text-sm text-gray-400">System Design, Architecture</p>
            </div>
            
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <Code className="text-3xl text-neon-pink mb-4 mx-auto" />
              <h3 className="font-bold mb-2">Full Stack Developer</h3>
              <p className="text-sm text-gray-400">End-to-end Development</p>
            </div>
            
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <Triangle className="text-3xl text-cyber-purple mb-4 mx-auto" />
              <h3 className="font-bold mb-2">Blockchain Developer</h3>
              <p className="text-sm text-gray-400">Smart Contracts, DeFi, Web3</p>
            </div>
            
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <Brain className="text-3xl text-cyber-gold mb-4 mx-auto" />
              <h3 className="font-bold mb-2">AI Engineer</h3>
              <p className="text-sm text-gray-400">Machine Learning, AI/ML</p>
            </div>
            
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <Activity className="text-3xl text-cyber-blue mb-4 mx-auto" />
              <h3 className="font-bold mb-2">Data Scientist</h3>
              <p className="text-sm text-gray-400">Analytics, ML Models</p>
            </div>
            
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <FileText className="text-3xl text-neon-pink mb-4 mx-auto" />
              <h3 className="font-bold mb-2">Data Analyst</h3>
              <p className="text-sm text-gray-400">Business Intelligence, Reports</p>
            </div>
            
            <div className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
              <Cloud className="text-3xl text-cyber-purple mb-4 mx-auto" />
              <h3 className="font-bold mb-2">Cloud Engineer</h3>
              <p className="text-sm text-gray-400">AWS, Azure, GCP</p>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-8">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="glassmorphism rounded-3xl p-8 cyber-border">
              <Shield className="text-6xl text-cyber-gold mb-6 mx-auto" />
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Our Guarantee</h2>
              <p className="text-2xl md:text-3xl font-bold text-cyber-gold mb-6">
                Only pay when you hire. No subscription.
              </p>
              <p className="text-lg text-gray-300 mb-6">
                We're so confident in our service that we only get paid when you successfully hire a developer. 
                No upfront costs, no monthly subscriptions, no hidden fees.
              </p>
              <Link href="/project-form">
                <Button 
                  className="bg-black hover:bg-gradient-to-r hover:from-neon-pink hover:to-cyber-purple text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 border-2 border-cyber-purple"
                >
                  Start Risk-Free Today
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-8 bg-gradient-to-r from-cyber-purple via-cyber-blue to-neon-pink">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Ready to Scale Your Team?</h2>
          <p className="text-xl mb-6 opacity-90">Join hundreds of funded startups who trust UnicornClub.io for their hiring needs</p>
          <Link href="/project-form">
            <Button 
              className="bg-white text-cyber-purple hover:bg-gray-100 font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl"
            >
              <Calendar className="mr-3" />
              Book Your Free Discovery Call
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-dark-slate py-16 border-t border-gray-800">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            <div className="md:col-span-1 lg:col-span-1">
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src={logoImage} 
                  alt="UnicornClub Logo" 
                  className="w-10 h-10 object-contain flex-shrink-0"
                />
                <span className="text-2xl font-bold text-white">UnicornClub</span>
              </div>
              <p className="text-gray-400 mb-6 max-w-sm">Connecting funded FinTech startups with elite South African IT talent.</p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-cyber-purple transition-colors">
                  <Users className="text-xl" />
                </a>
                <a href="#" className="text-gray-400 hover:text-cyber-purple transition-colors">
                  <Users className="text-xl" />
                </a>
                <a href="#" className="text-gray-400 hover:text-cyber-purple transition-colors">
                  <Users className="text-xl" />
                </a>
              </div>
            </div>
            
            <div className="md:col-span-1 lg:col-span-1">
              <h4 className="font-bold mb-4 text-white">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link href="/about" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})} className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link href="/howitworks" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})} className="hover:text-white transition-colors">How It Works</Link></li>
                <li><Link href="/faq" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})} className="hover:text-white transition-colors">FAQ</Link></li>
                <li><Link href="/blog" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})} className="hover:text-white transition-colors">Blog</Link></li>
              </ul>
            </div>
            
            
            <div className="md:col-span-1 lg:col-span-1">
              <h4 className="font-bold mb-4 text-white">Contact</h4>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center">
                  <Mail className="mr-2 w-4 h-4 flex-shrink-0" />
                  <span className="break-all">info@unicornclub.io</span>
                </li>
                <li className="flex items-center">
                  <Phone className="mr-2 w-4 h-4 flex-shrink-0" />
                  <span>+27219134061</span>
                </li>
                <li className="flex items-start">
                  <MapPin className="mr-2 w-4 h-4 mt-1 flex-shrink-0" />
                  <span className="text-sm leading-relaxed">First Floor, Willowbridge Centre, Bellville Park, Carl Cronje Dr, Cape Town, WC, South Africa 7530</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2024 UnicornClub.io. All rights reserved. | <a href="#" className="hover:text-white">Privacy Policy</a> | <a href="#" className="hover:text-white">Terms of Service</a></p>
          </div>
        </div>
      </footer>

      {/* Sticky CTA Button */}
      {showStickyBtn && (
        <div className="fixed bottom-6 right-6 z-50 transition-all duration-300">
          <Link href="/project-form">
            <Button 
              className="bg-cyber-gradient hover:bg-gradient-to-r hover:from-neon-pink hover:to-cyber-purple text-white font-bold py-4 px-6 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-105 animate-pulse"
            >
              <Calendar className="mr-2" />
              Book Call
            </Button>
          </Link>
        </div>
      )}

      {/* Project Form Modal */}
      <Dialog open={showProjectForm} onOpenChange={setShowProjectForm}>
        <DialogContent className="bg-charcoal rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto border-gray-600" aria-describedby="project-form-description">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">Start Your Project</DialogTitle>
            <DialogDescription id="project-form-description" className="text-gray-300">
              Submit your project requirements and book a discovery call with our team
            </DialogDescription>
          </DialogHeader>
          
          <ProjectForm onClose={closeProjectForm} />
        </DialogContent>
      </Dialog>



    </div>
  );
}
